import { Renderer, Program, Mesh, Triangle, type OGLRenderingContext } from "ogl";
import { ORB_VERTEX, orbFragment } from "./OrbShader.js";
import { OrbMotionController } from "./OrbMotionController.js";
import type { OrbAudioFrame, OrbState } from "./orb.types.js";

export interface OrbRendererOptions {
  container: HTMLElement;
  getState: () => OrbState;
  /** The voice's level and band energies, read once per frame. */
  getAudio: () => OrbAudioFrame;
  reducedMotion: boolean;
}

/** Whether a WebGL context can be created at all (checked once, before mounting the canvas). */
export function webglAvailable(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl") ?? c.getContext("experimental-webgl"));
  } catch {
    return false;
  }
}

/**
 * Draws the orb with a single full-canvas triangle and a fragment shader. It runs its own
 * requestAnimationFrame loop and keeps every high-frequency value in uniforms, so React never
 * re-renders per frame.
 */
export class OrbRenderer {
  private readonly renderer: Renderer;
  private readonly gl: OGLRenderingContext;
  private readonly program: Program;
  private readonly mesh: Mesh;
  private readonly motion = new OrbMotionController();
  private readonly resizeObserver: ResizeObserver;
  private readonly visibility: IntersectionObserver;
  private raf = 0;
  private running = false;
  private visible = true;
  private last = 0;
  private time = 0;
  private dpr: number;
  private slowFrames = 0;
  private frames = 0;
  private readonly onLost = (e: Event) => { e.preventDefault(); this.stop(); };

  constructor(private readonly opts: OrbRendererOptions) {
    const small = Math.min(opts.container.clientWidth, window.innerWidth) < 360;
    this.dpr = Math.min(window.devicePixelRatio || 1, small ? 1.5 : 2);

    this.renderer = new Renderer({ alpha: true, premultipliedAlpha: true, antialias: false, dpr: this.dpr });
    this.gl = this.renderer.gl;
    this.gl.clearColor(0, 0, 0, 0);
    this.gl.canvas.classList.add("block", "size-full");
    opts.container.appendChild(this.gl.canvas as HTMLCanvasElement);
    this.gl.canvas.addEventListener("webglcontextlost", this.onLost);

    const lowPower = small || /Android|iPhone|iPad/i.test(navigator.userAgent);
    this.program = new Program(this.gl, {
      vertex: ORB_VERTEX,
      fragment: orbFragment(lowPower ? 3 : 4),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 }, uScale: { value: 1 }, uTurb: { value: 0.25 }, uFlow: { value: 0.55 },
        uGlow: { value: 0.55 }, uRim: { value: 0.75 }, uBright: { value: 0.92 }, uAudio: { value: 0 },
        uHue: { value: 0 }, uBands: { value: [0, 0, 0] },
      },
    });
    this.mesh = new Mesh(this.gl, { geometry: new Triangle(this.gl), program: this.program });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(opts.container);
    // No point drawing while scrolled away.
    this.visibility = new IntersectionObserver(([entry]) => { this.visible = entry?.isIntersecting ?? true; });
    this.visibility.observe(opts.container);
    this.resize();
  }

  private resize() {
    const { clientWidth: w, clientHeight: h } = this.opts.container;
    if (w > 0 && h > 0) this.renderer.setSize(w, h);
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private readonly frame = (now: number) => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);
    if (!this.visible || document.hidden) { this.last = now; return; }

    const dt = Math.min((now - this.last) / 1000, 0.1);
    this.last = now;
    this.adaptQuality(dt);

    // Reduced motion: the orb still lives, but everything runs far slower and ignores the voice's wobble.
    const calm = this.opts.reducedMotion;
    const m = this.motion.update(dt, this.opts.getState(), calm ? { level: 0, low: 0, mid: 0, high: 0 } : this.opts.getAudio());
    this.time += dt * (calm ? 0.2 : 1);

    const u = this.program.uniforms;
    u["uTime"]!.value = this.time;
    u["uScale"]!.value = m.scale;
    u["uTurb"]!.value = calm ? m.turbulence * 0.4 : m.turbulence;
    u["uFlow"]!.value = m.flowSpeed;
    u["uGlow"]!.value = m.glowStrength;
    u["uRim"]!.value = m.rimStrength;
    u["uBright"]!.value = m.brightness;
    u["uAudio"]!.value = m.audioIntensity;
    u["uHue"]!.value = m.hue;
    u["uBands"]!.value = [m.bandLow, m.bandMid, m.bandHigh];

    this.renderer.render({ scene: this.mesh });
  };

  // If frames keep taking too long, render fewer pixels. Sustained, not a single hiccup.
  private adaptQuality(dt: number) {
    this.frames++;
    if (dt > 0.026) this.slowFrames++;
    if (this.frames < 90) return;
    if (this.slowFrames > 45 && this.dpr > 1) {
      this.dpr = Math.max(1, this.dpr - 0.25);
      this.renderer.dpr = this.dpr;
      this.resize();
    }
    this.frames = 0;
    this.slowFrames = 0;
  }

  destroy() {
    this.stop();
    this.resizeObserver.disconnect();
    this.visibility.disconnect();
    this.gl.canvas.removeEventListener("webglcontextlost", this.onLost);
    (this.gl.canvas as HTMLCanvasElement).remove();
    // Free the GPU context straight away instead of waiting for garbage collection.
    this.gl.getExtension("WEBGL_lose_context")?.loseContext();
  }
}
